/**
 * Profile score rules — keep in sync with
 * `recompute_subscriber_profile_score` in
 * supabase/migrations/20260519120000_ai_email_personalization.sql
 */

import type { ProfileScoreTier } from './personalization';
import { tierLabel } from './personalization';

export type ProfileScoreSignal = {
  id: string;
  label: string;
  points: number;
  description: string;
  source: 'subscriber' | 'connection' | 'transcript';
};

export const PROFILE_SCORE_SIGNALS: ProfileScoreSignal[] = [
  {
    id: 'email',
    label: 'Email on file',
    points: 5,
    description: 'Subscriber has a non-empty email address.',
    source: 'subscriber',
  },
  {
    id: 'name',
    label: 'Name on file',
    points: 15,
    description: 'Subscriber has a name set (not blank).',
    source: 'subscriber',
  },
  {
    id: 'channels',
    label: 'At least one channel',
    points: 10,
    description: 'Any channel flag is true in the subscriber channels map.',
    source: 'subscriber',
  },
  {
    id: 'connection_match',
    label: 'Connection match',
    points: 10,
    description: 'A connection row exists with the same email (most recently updated wins).',
    source: 'connection',
  },
  {
    id: 'icp',
    label: 'ICP on connection',
    points: 20,
    description: 'Matched connection has an ICP value filled in.',
    source: 'connection',
  },
  {
    id: 'role',
    label: 'Role on connection',
    points: 5,
    description: 'Matched connection has a role filled in.',
    source: 'connection',
  },
  {
    id: 'linkedin_url',
    label: 'LinkedIn on connection',
    points: 5,
    description: 'Matched connection has a LinkedIn URL.',
    source: 'connection',
  },
  {
    id: 'call_me_when',
    label: 'Call me when (any)',
    points: 10,
    description: 'Any of callMeWhen1–3 is set on the matched connection.',
    source: 'connection',
  },
  {
    id: 'transcripts',
    label: 'Transcript participation',
    points: 20,
    description: 'At least one transcript includes this email as a participant.',
    source: 'transcript',
  },
];

export const PROFILE_SCORE_MAX = 100;

export const PROFILE_SCORE_TIERS: Array<{
  tier: ProfileScoreTier;
  min: number;
  max: number;
  description: string;
}> = [
  {
    tier: 'none',
    min: 0,
    max: 19,
    description: 'Very little signal for AI personalization.',
  },
  {
    tier: 'minimal',
    min: 20,
    max: 39,
    description: 'Basic identity only; limited context for tailored copy.',
  },
  {
    tier: 'basic',
    min: 40,
    max: 59,
    description: 'Enough context for light personalization.',
  },
  {
    tier: 'rich',
    min: 60,
    max: 100,
    description: 'Strong connection and/or transcript signal.',
  },
];

export const PROFILE_SCORE_NOTES = [
  'Scores are recomputed via the Score button on a subscriber, or automatically when subscriber/connection/custom field data changes.',
  'The breakdown is stored on each subscriber as profile_score_breakdown (signal id → points).',
  'Automations and newsletters can enable AI personalization with a minimum score (default 50). Sends below that threshold use the base subject/body without AI rewrite.',
  'Custom fields can add points when configured with a profile weight on the field definition and the field has a filled value.',
];

export function maxPossibleProfileScore(): number {
  return PROFILE_SCORE_SIGNALS.reduce((sum, s) => sum + s.points, 0);
}

export function tierForScore(score: number): ProfileScoreTier {
  if (score < 20) return 'none';
  if (score < 40) return 'minimal';
  if (score < 60) return 'basic';
  return 'rich';
}

export function formatTierRange(tier: (typeof PROFILE_SCORE_TIERS)[number]): string {
  return `${tier.min}–${tier.max} → ${tierLabel(tier.tier)}`;
}
